import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api, API } from '../App';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, DollarSign, Calendar, Pencil, Trash2, Filter, Download } from 'lucide-react';
import { DatePickerField } from '../components/DatePickerField';
import { format, subDays, startOfWeek, startOfDay, isAfter, isBefore, parseISO } from 'date-fns';

const EXPENSE_CATEGORIES = ['Fuel', 'Labour', 'Packaging', 'Repairs', 'Utilities', 'Contract Milling', 'Lunch & Supper', 'Directors', 'Commissions', 'Packaging Labour', 'Roylex Expenses', 'Asset Purchases', 'Truck & Repairs', 'Truck Drivers', 'Government & Kanjo', 'Offloading Labour', 'Amenities', 'Weighbridges', 'Misc', 'Transport', 'Taping'];
const ALL_DISPLAY_CATEGORIES = ['Fuel', 'Labour', 'Packaging', 'Repairs', 'Utilities', 'Contract Milling', 'Lunch & Supper', 'Directors', 'Commissions', 'Packaging Labour', 'Roylex Expenses', 'Asset Purchases', 'Truck & Repairs', 'Truck Drivers', 'Government & Kanjo', 'Offloading Labour', 'Amenities', 'Weighbridges', 'Orders', 'Misc', 'Transport', 'Taping'];

export default function Expenses() {
  const { isAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [batchLinks, setBatchLinks] = useState({}); // expense_id -> [{purchase_id, batch_no, batch_date, factory}]
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [date, setDate] = useState(new Date());
  const [formData, setFormData] = useState({
    category: '',
    amount: '',
    remarks: ''
  });

  // Filter state
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [filterTag, setFilterTag] = useState('all'); // Batch Tag: all | tagged | untagged
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  useEffect(() => {
    fetchExpenses();
    fetchBatchLinks();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      // Sort by date DESC (newest first)
      const sorted = response.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setExpenses(sorted);
    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchBatchLinks = async () => {
    try {
      const response = await api.get('/expenses/batch-links');
      setBatchLinks(response.data || {});
    } catch (error) {
      // Non-fatal — links map defaults to empty
      setBatchLinks({});
    }
  };

  // Filter expenses based on category, period, and batch tag
  const filteredExpenses = useMemo(() => {
    let filtered = [...expenses];

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(e => e.category === filterCategory);
    }

    // Batch Tag filter — derived from live batchLinks map (never stale)
    if (filterTag === 'tagged') {
      filtered = filtered.filter(e => (batchLinks[e.id] || []).length > 0);
    } else if (filterTag === 'untagged') {
      filtered = filtered.filter(e => (batchLinks[e.id] || []).length === 0);
    }

    // Period filter
    const today = startOfDay(new Date());
    if (filterPeriod === 'today') {
      filtered = filtered.filter(e => {
        const expDate = parseISO(e.date);
        return format(expDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd');
      });
    } else if (filterPeriod === 'week') {
      const weekStart = startOfWeek(today, { weekStartsOn: 1 });
      filtered = filtered.filter(e => {
        const expDate = parseISO(e.date);
        return !isBefore(expDate, weekStart);
      });
    } else if (filterPeriod === 'month') {
      const monthStart = subDays(today, 30);
      filtered = filtered.filter(e => {
        const expDate = parseISO(e.date);
        return !isBefore(expDate, monthStart);
      });
    } else if (filterPeriod === 'custom' && customStartDate && customEndDate) {
      filtered = filtered.filter(e => {
        const expDate = parseISO(e.date);
        return !isBefore(expDate, startOfDay(customStartDate)) && !isAfter(expDate, startOfDay(customEndDate));
      });
    }

    return filtered;
  }, [expenses, filterCategory, filterPeriod, filterTag, batchLinks, customStartDate, customEndDate]);

  // Calculate totals from filtered expenses
  const categoryTotals = useMemo(() => {
    return filteredExpenses.reduce((acc, e) => {
      acc[e.category] = (acc[e.category] || 0) + e.amount;
      return acc;
    }, {});
  }, [filteredExpenses]);

  const totalExpenses = useMemo(() => {
    return filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [filteredExpenses]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.category || !formData.amount) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      const payload = {
        date: format(date, 'yyyy-MM-dd'),
        category: formData.category,
        amount: parseFloat(formData.amount),
        remarks: formData.remarks || null
      };

      if (editingExpense) {
        await api.put(`/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await api.post('/expenses', payload);
        toast.success('Expense recorded');
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchBatchLinks();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const resetForm = () => {
    setFormData({ category: '', amount: '', remarks: '' });
    setDate(new Date());
    setEditingExpense(null);
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      amount: expense.amount.toString(),
      remarks: expense.remarks || ''
    });
    setDate(new Date(expense.date));
    setDialogOpen(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterPeriod('all');
    setFilterTag('all');
    setCustomStartDate(null);
    setCustomEndDate(null);
  };

  // Compute applied date range for backend (matches client filter)
  const computedRange = useMemo(() => {
    const today = startOfDay(new Date());
    if (filterPeriod === 'today') return { start: format(today, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    if (filterPeriod === 'week') {
      const ws = startOfWeek(today, { weekStartsOn: 1 });
      return { start: format(ws, 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    }
    if (filterPeriod === 'month') {
      return { start: format(subDays(today, 30), 'yyyy-MM-dd'), end: format(today, 'yyyy-MM-dd') };
    }
    if (filterPeriod === 'custom' && customStartDate && customEndDate) {
      return { start: format(customStartDate, 'yyyy-MM-dd'), end: format(customEndDate, 'yyyy-MM-dd') };
    }
    return { start: null, end: null };
  }, [filterPeriod, customStartDate, customEndDate]);

  const [downloading, setDownloading] = useState(false);
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (computedRange.start) params.append('start_date', computedRange.start);
      if (computedRange.end) params.append('end_date', computedRange.end);
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterTag !== 'all') params.append('tag', filterTag);
      const url = `${API}/expenses/report/pdf${params.toString() ? '?' + params.toString() : ''}`;
      const token = localStorage.getItem('token');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `Expenses_${format(new Date(), 'yyyyMMdd')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(dl);
      toast.success('PDF downloaded');
    } catch {
      toast.error('Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#1B4D3E]">Expenses</h1>
          <p className="text-slate-500 mt-1">Track operational expenses</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" className="gap-2 flex-1 sm:flex-none" onClick={handleDownloadPDF} disabled={downloading} data-testid="download-expenses-pdf">
            <Download className="h-4 w-4" /> {downloading ? 'Downloading…' : 'Download PDF'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary gap-2 flex-1 sm:flex-none" data-testid="add-expense-btn">
                <Plus className="h-4 w-4" />
                Add Expense
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingExpense ? 'Edit Expense' : 'Record Expense'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <DatePickerField date={date} onDateChange={setDate} />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount (KES) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Remarks</Label>
                <Input
                  value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" data-testid="expense-submit-btn">
                  {editingExpense ? 'Update' : 'Record'} Expense
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-slate-500" />
              <Label>Category:</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40" data-testid="filter-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {ALL_DISPLAY_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Period:</Label>
              <Select value={filterPeriod} onValueChange={setFilterPeriod}>
                <SelectTrigger className="w-36" data-testid="filter-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Batch Tag:</Label>
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger className="w-32" data-testid="filter-tag">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="tagged">Tagged</SelectItem>
                  <SelectItem value="untagged">Untagged</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {filterPeriod === 'custom' && (
              <>
                <div className="flex items-center gap-2">
                  <Label>From:</Label>
                  <DatePickerField
                    date={customStartDate}
                    onDateChange={setCustomStartDate}
                    buttonClassName="w-32 justify-start text-left font-normal text-sm"
                    displayFormat="MMM d"
                    placeholder="Start"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label>To:</Label>
                  <DatePickerField
                    date={customEndDate}
                    onDateChange={setCustomEndDate}
                    buttonClassName="w-32 justify-start text-left font-normal text-sm"
                    displayFormat="MMM d"
                    placeholder="End"
                  />
                </div>
              </>
            )}
            {(filterCategory !== 'all' || filterPeriod !== 'all' || filterTag !== 'all') && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500">
                Clear Filters
              </Button>
            )}
            <div className="ml-auto text-sm text-slate-500">
              Showing {filteredExpenses.length} of {expenses.length} expenses
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {ALL_DISPLAY_CATEGORIES.map((cat) => (
          <Card key={cat} className={cat === 'Orders' ? 'border-blue-200 bg-blue-50' : ''}>
            <CardContent className="p-4">
              <p className={`text-xs uppercase tracking-wider ${cat === 'Orders' ? 'text-blue-600' : 'text-slate-500'}`}>{cat}</p>
              <p className={`text-xl font-bold mt-1 ${cat === 'Orders' ? 'text-blue-700' : 'text-slate-900'}`}>
                {formatCurrency(categoryTotals[cat] || 0)}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Total */}
      <Card className="bg-orange-50 border-orange-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-orange-700">Total Expenses {filterCategory !== 'all' || filterPeriod !== 'all' || filterTag !== 'all' ? '(Filtered)' : ''}</p>
                <p className="text-3xl font-bold text-orange-600">
                  {formatCurrency(totalExpenses)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="table-header">
                <TableHead>Date</TableHead>
                <TableHead>Category</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Tag / Batch</TableHead>
                <TableHead>Remarks</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No expenses found {filterCategory !== 'all' || filterPeriod !== 'all' || filterTag !== 'all' ? 'matching filters' : ''}
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => {
                  const links = batchLinks[expense.id] || [];
                  const isTagged = links.length > 0;
                  return (
                  <TableRow key={expense.id} className="table-row" data-testid={`expense-row-${expense.id}`}>
                    <TableCell>{expense.date}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded text-sm ${expense.category === 'Orders' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'}`}>
                        {expense.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">{formatCurrency(expense.amount)}</TableCell>
                    <TableCell data-testid={`expense-tag-${expense.id}`}>
                      {!isTagged ? (
                        <span className="text-xs text-slate-400 italic">Untagged</span>
                      ) : links.length === 1 ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800 w-fit">Tagged</span>
                          <Link
                            to={`/batches/${links[0].purchase_id}`}
                            className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                            data-testid={`expense-batch-link-${expense.id}`}
                          >
                            {links[0].batch_no}
                            {links[0].batch_date ? <span className="text-slate-400 ml-1">· {links[0].batch_date}</span> : null}
                          </Link>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-0.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-100 text-emerald-800 w-fit">Tagged ({links.length})</span>
                          <div className="flex flex-wrap gap-1">
                            {links.map((l, i) => (
                              <Link
                                key={l.purchase_id}
                                to={`/batches/${l.purchase_id}`}
                                className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline"
                                data-testid={`expense-batch-link-${expense.id}-${i}`}
                              >
                                {l.batch_no}{i < links.length - 1 ? ',' : ''}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">{expense.remarks || '-'}</TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(expense)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(expense.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
